import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { palette, admin, fontFamily } from '../../styles/tokens';

export interface ShortcutItem {
  key: string;
  description: string;
}

interface Props {
  shortcuts: ShortcutItem[];
  triggerKey?: string;
  enabled?: boolean;
}

export function KeyboardShortcutHelp({ shortcuts, triggerKey = '?', enabled = true }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      if (e.key === triggerKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setIsOpen(o => !o);
        return;
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, triggerKey, enabled]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-label="Keyboard shortcuts"
      aria-modal="true"
      onClick={() => setIsOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 360, maxWidth: 'calc(100vw - 32px)',
          background: palette.white,
          borderRadius: 12,
          border: `1px solid ${admin.border}`,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          padding: '16px 18px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Keyboard shortcuts</div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close shortcuts"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: 6,
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: palette.g500,
            }}
          >
            <X size={15} strokeWidth={1.8} />
          </button>
        </div>
        <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shortcuts.map(s => (
            <li key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 13, color: palette.g700 }}>{s.description}</span>
              <kbd style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11.5, fontWeight: 600,
                color: palette.g700,
                background: palette.g100,
                border: `1px solid ${palette.g200}`,
                borderRadius: 5,
                padding: '2px 8px',
                minWidth: 28, textAlign: 'center',
              }}>{s.key}</kbd>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${palette.g100}`, fontSize: 11.5, color: palette.g400 }}>
          Press <kbd style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, padding: '1px 5px', background: palette.g100, borderRadius: 3 }}>Esc</kbd> to close
        </div>
      </div>
    </div>
  );
}
